import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CollectionCard = ({ col, index, big = false }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      className={`cc-card ${big ? 'cc-card--big' : ''} ${col.is_luxe ? 'cc-card--luxe' : ''}`}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: (index % 4) * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      <Link to={`/collections/${col.slug}`} className="cc-link">
        <div className="cc-img-wrap">
          <img src={col.image_url} alt={col.name} loading="lazy" />
          <div className="cc-img-overlay" />
        </div>
        {col.badge && <span className="cc-badge">{col.badge}</span>}
        <div className="cc-text">
          <p className="cc-sub">{col.sub}</p>
          <h3 className="cc-name">{col.name}</h3>
          <span className="cc-explore">
            Explore
            <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
              <path d="M1 5h12M8 1l4 4-4 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

const CollectionsPage = () => {
  const heroRef  = useRef(null);
  const heroView = useInView(heroRef, { once: true });
  const [collections, setCollections] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');

  useEffect(() => {
    document.title = 'Collections | Aamira';
    fetch(`${API}/collections`)
      .then(r => r.json())
      .then(data => {
        if (data.success) setCollections(data.collections);
        else setError(data.error || 'Failed to load');
      })
      .catch(() => setError('Failed to connect to server'))
      .finally(() => setLoading(false));
  }, []);

  const FEATURED = collections.filter(c => c.is_featured);
  const REGULAR  = collections.filter(c => !c.is_featured);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Jost:wght@300;400;500;600&family=Cormorant+Garamond:ital,wght@0,300;1,300&display=swap');

        .cp-page {
          background: #FAF8F4; min-height: 100vh;
          padding-top: 162px; font-family: 'Jost', sans-serif;
          position: relative; overflow-x: hidden;
        }
        .cp-page::before {
          content: ''; position: fixed; inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.022'/%3E%3C/svg%3E");
          pointer-events: none; z-index: 0;
        }
        .cp-hero {
          position: relative; z-index: 1; text-align: center;
          padding: 56px clamp(20px, 5vw, 80px) 60px;
          border-bottom: 1px solid rgba(176,141,87,0.15);
        }
        .cp-hero-eyebrow {
          font-size: 10px; font-weight: 500; letter-spacing: 0.35em;
          text-transform: uppercase; color: #B08D57;
          display: flex; align-items: center; justify-content: center;
          gap: 14px; margin-bottom: 20px;
        }
        .cp-hero-eyebrow::before, .cp-hero-eyebrow::after {
          content: ''; width: 40px; height: 1px;
          background: linear-gradient(to right, transparent, #B08D57);
        }
        .cp-hero-eyebrow::after { background: linear-gradient(to left, transparent, #B08D57); }
        .cp-hero-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(38px, 6vw, 72px); font-weight: 700;
          color: #1C1A18; line-height: 1.0; letter-spacing: -0.02em; margin-bottom: 8px;
        }
        .cp-hero-title em { font-style: italic; font-weight: 400; color: #B08D57; display: block; }
        .cp-hero-desc {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(15px, 1.8vw, 19px); font-style: italic;
          font-weight: 300; color: #8A7E72; letter-spacing: 0.03em; margin-top: 14px;
        }
        .cp-count {
          display: inline-flex; align-items: center; gap: 6px; margin-top: 20px;
          font-size: 10px; font-weight: 500; letter-spacing: 0.2em;
          text-transform: uppercase; color: #B08D57;
          border: 1px solid rgba(176,141,87,0.3); padding: 6px 16px; border-radius: 100px;
        }
        .cp-inner {
          position: relative; z-index: 1; max-width: 1280px; margin: 0 auto;
          padding: 0 clamp(16px, 4vw, 64px) 100px;
        }
        .cp-section-label {
          display: flex; align-items: center; gap: 14px; margin: 32px 0 10px;
        }
        .cp-section-label span {
          font-size: 10px; font-weight: 600; letter-spacing: 0.3em;
          text-transform: uppercase; color: #B08D57; white-space: nowrap;
        }
        .cp-section-label::after {
          content: ''; flex: 1; height: 1px;
          background: linear-gradient(to right, rgba(176,141,87,0.35), transparent);
        }
        .cp-featured-grid {
          display: grid; grid-template-columns: 1fr 1fr;
          grid-template-rows: 420px 340px; gap: 10px;
        }
        .cc-card--luxe { grid-column: 1 / -1 !important; }
        .cp-regular-grid {
          display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px;
        }
        .cc-card { position: relative; border-radius: 2px; overflow: hidden; }
        .cc-link { display: block; width: 100%; height: 100%; text-decoration: none; position: relative; }
        .cc-card--big .cc-link { height: 100%; }
        .cc-card--big .cc-img-wrap { height: 100%; }
        .cc-card:not(.cc-card--big) .cc-img-wrap { height: 260px; }
        .cc-img-wrap { position: relative; overflow: hidden; width: 100%; }
        .cc-img-wrap img {
          width: 100%; height: 100%; object-fit: cover; display: block;
          transition: transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .cc-card:hover .cc-img-wrap img { transform: scale(1.07); }
        .cc-img-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(to top, rgba(14,11,6,0.82) 0%, rgba(14,11,6,0.28) 45%, rgba(14,11,6,0.03) 100%);
          opacity: 0.65;
          transition: opacity 0.35s;
        }
        .cc-card:hover .cc-img-overlay { opacity: 0.72; }
        .cc-badge {
          position: absolute; top: 14px; right: 14px; z-index: 3;
          font-size: 8.5px; font-weight: 600; letter-spacing: 0.18em;
          text-transform: uppercase; color: #1C1A18; background: #B08D57;
          padding: 5px 11px; border-radius: 1px;
        }
        .cc-text {
          position: absolute; bottom: 0; left: 0; right: 0; z-index: 3;
          padding: 20px 20px 22px; transform: translateY(6px); transition: transform 0.35s ease;
        }
        .cc-card:hover .cc-text { transform: translateY(0); }
        .cc-sub {
          font-family: 'Jost', sans-serif; font-size: 9px; font-weight: 500;
          letter-spacing: 0.25em; text-transform: uppercase; color: #B08D57;
          margin-bottom: 5px; opacity: 0.9;
        }
        .cc-name {
          font-family: 'Playfair Display', serif;
          font-size: clamp(16px, 2vw, 22px); font-weight: 700;
          color: #FAF8F4; line-height: 1.15; letter-spacing: -0.01em; margin-bottom: 10px;
        }
        .cc-card--luxe .cc-name {
          font-size: clamp(22px, 3.5vw, 38px); font-style: italic;
          font-weight: 400; color: #D4AA70;
        }
        .cc-card--luxe .cc-sub { font-size: 10px; letter-spacing: 0.3em; }
        .cc-explore {
          display: inline-flex; align-items: center; gap: 7px;
          font-size: 9px; font-weight: 600; letter-spacing: 0.2em;
          text-transform: uppercase; color: rgba(250,248,244,0.55); transition: color 0.25s, gap 0.25s;
        }
        .cc-card:hover .cc-explore { color: #B08D57; gap: 10px; }
        .cp-loading {
          text-align: center; padding: 80px; font-size: 13px;
          color: #9A9186; letter-spacing: 0.1em;
        }
        .cp-error {
          text-align: center; padding: 40px; color: #C0392B; font-size: 13px;
        }
        .cp-empty {
          text-align: center; padding: 80px 20px; color: #9A9186;
        }
        .cp-empty-title {
          font-family: 'Playfair Display', serif; font-size: 22px;
          color: #3a3530; margin-bottom: 8px;
        }
        .cp-ornament {
          display: flex; align-items: center; justify-content: center;
          gap: 16px; margin-top: 64px; opacity: 0.35;
        }
        .cp-ornament::before { content: ''; width: 80px; height: 1px; background: linear-gradient(to right, transparent, #B08D57); }
        .cp-ornament::after  { content: ''; width: 80px; height: 1px; background: linear-gradient(to left, transparent, #B08D57); }
        .cp-diamond { width: 6px; height: 6px; background: #B08D57; transform: rotate(45deg); }
        @media (max-width: 1024px) { .cp-regular-grid { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 760px) {
          .cp-page { padding-top: 148px; }
          .cp-featured-grid { grid-template-columns: 1fr 1fr; grid-template-rows: 280px 220px; }
          .cp-regular-grid { grid-template-columns: repeat(2, 1fr); }
          .cc-card:not(.cc-card--big) .cc-img-wrap { height: 200px; }
        }
        @media (max-width: 480px) {
          .cp-page { padding-top: 116px; }
          .cp-featured-grid { grid-template-columns: 1fr; grid-template-rows: auto; }
          .cp-featured-grid .cc-card--big .cc-img-wrap { height: 300px; }
          .cc-card--luxe { grid-column: auto !important; }
          .cp-regular-grid { grid-template-columns: repeat(2, 1fr); }
          .cc-card:not(.cc-card--big) .cc-img-wrap { height: 170px; }
        }
      `}</style>

      <div className="cp-page">
        <div className="cp-hero" ref={heroRef}>
          <motion.p className="cp-hero-eyebrow"
            initial={{ opacity: 0 }} animate={heroView ? { opacity: 1 } : {}} transition={{ duration: 0.6 }}>
            Aamira Jewellery
          </motion.p>
          <motion.h1 className="cp-hero-title"
            initial={{ opacity: 0, y: 24 }} animate={heroView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.12 }}>
            Our<em>Collections</em>
          </motion.h1>
          <motion.p className="cp-hero-desc"
            initial={{ opacity: 0 }} animate={heroView ? { opacity: 1 } : {}} transition={{ duration: 0.6, delay: 0.28 }}>
            Curated with care — jewellery for every woman, every occasion.
          </motion.p>
          <motion.div className="cp-count"
            initial={{ opacity: 0, scale: 0.95 }} animate={heroView ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.5, delay: 0.42 }}>
            ✦ &nbsp;{collections.length} Collections
          </motion.div>
        </div>

        <div className="cp-inner">
          {loading ? (
            <div className="cp-loading">Loading collections…</div>
          ) : error ? (
            <div className="cp-error">{error}</div>
          ) : collections.length === 0 ? (
            <div className="cp-empty">
              <div className="cp-empty-title">No collections yet</div>
              <p>Check back soon.</p>
            </div>
          ) : (
            <>
              {FEATURED.length > 0 && (
                <>
                  <div className="cp-section-label"><span>Featured</span></div>
                  <div className="cp-featured-grid">
                    {FEATURED.map((col, i) => (
                      <CollectionCard key={col.id} col={col} index={i} big />
                    ))}
                  </div>
                </>
              )}

              {REGULAR.length > 0 && (
                <>
                  <div className="cp-section-label"><span>All Collections</span></div>
                  <div className="cp-regular-grid">
                    {REGULAR.map((col, i) => (
                      <CollectionCard key={col.id} col={col} index={i} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          <motion.div className="cp-ornament"
            initial={{ opacity: 0 }} whileInView={{ opacity: 0.35 }}
            viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <div className="cp-diamond" />
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default CollectionsPage;